// src/pages/MyCharactersPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getUserCharacters, deleteCharacter } from '../services/characterService';
import { Character } from '../types/character';
import { PhotoIcon, PlusIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import CharacterCreateWizard from '../components/character/CharacterCreateWizard';

const MyCharactersPage: React.FC = () => {
  const { t } = useTranslation('character');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [characters, setCharacters] = useState<Character[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Character | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMyCharacters = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const data = await getUserCharacters(user.uid);
      setCharacters(data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Handle delete button click
  const handleDeleteClick = useCallback((e: React.MouseEvent, character: Character) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(character);
    setShowDeleteConfirm(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteCharacter(deleteTarget.id);

      // Clear feed cache to remove deleted character
      const cacheKeys = Object.keys(localStorage).filter(key => key.startsWith('oc_feed_'));
      cacheKeys.forEach(key => localStorage.removeItem(key));

      // Remove from local state
      setCharacters(prev => prev.filter(c => c.id !== deleteTarget.id));
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting character:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, isDeleting]);

  useEffect(() => {
    if (user) {
      fetchMyCharacters();
    } else {
      setIsLoading(false);
    }
  }, [user, fetchMyCharacters]);

  if (!user) {
    return (
      <div
        className="py-20 flex items-center justify-center select-none"
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
      >
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('myCharacters.loginRequired')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-2 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            {t('myCharacters.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="select-none"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      {/* Title Section */}
      <div className="max-w-8xl pt-6 px-4 md:px-8 max-w-8xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('myCharacters.title')}
          </h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">{t('myCharacters.createNew')}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-8xl mx-auto px-4 md:px-8 pb-8">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
          </div>
        ) : characters.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {characters.map((character) => (
              <Link
                key={character.id}
                to={`/character/${character.id}`}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden group"
              >
                {/* Header Image */}
                <div className="h-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 overflow-hidden">
                  {character.headerImageUrl && (
                    <img
                      src={character.headerImageUrl}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  )}
                </div>

                {/* Profile */}
                <div className="p-6 -mt-10 relative">
                  <div className="w-20 h-20 rounded-full border-4 border-white dark:border-gray-800 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 overflow-hidden shadow-lg">
                    {(character.thumbnailUrl || character.avatarUrl) ? (
                      <img
                        src={character.thumbnailUrl || character.avatarUrl}
                        alt={character.name}
                        className="w-full h-full object-cover"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-2xl text-white font-bold">
                          {character.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-3">{character.name}</h3>

                  {character.bio && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 line-clamp-2">{character.bio}</p>
                  )}

                  <div className="flex gap-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>{character.stats?.followerCount || 0} {t('myCharacters.followers')}</span>
                    <span>{character.stats?.likeCount || 0} {t('myCharacters.likes')}</span>
                  </div>

                  {/* Status Badge and Delete Button */}
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    {character.status === 'active' ? (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                        {t('myCharacters.statusActive')}
                      </span>
                    ) : character.status === 'draft' ? (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                        {t('myCharacters.statusDraft')}
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
                        {t('myCharacters.statusSuspended')}
                      </span>
                    )}
                    <button
                      onClick={(e) => handleDeleteClick(e, character)}
                      className="p-1.5 bg-white/90 dark:bg-gray-700/90 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full shadow-sm transition-colors"
                      title={t('myCharacters.deleteButton')}
                    >
                      <TrashIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400" />
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <PhotoIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              {t('myCharacters.empty.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('myCharacters.empty.description')}
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-3 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              {t('myCharacters.empty.createFirst')}
            </button>
          </div>
        )}
      </div>

      {/* Character Create Wizard */}
      <CharacterCreateWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultCategory="original"
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !isDeleting && setShowDeleteConfirm(false)}
          />
          <div className="relative bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-white text-center mb-2">
              {t('myCharacters.deleteConfirm.title')}
            </h3>
            <p className="text-white/60 text-sm text-center mb-6">
              {t('myCharacters.deleteConfirm.message', { name: deleteTarget.name })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-white/10 text-white rounded-xl text-sm font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                {t('myCharacters.deleteConfirm.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t('myCharacters.deleteConfirm.deleting')}
                  </>
                ) : (
                  t('myCharacters.deleteConfirm.confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCharactersPage;
